import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Users, UserCheck, Trophy, Film, Tv, Clapperboard, Calendar, UserX, AlertCircle, Play, Volleyball, MicVocal } from 'lucide-react';
import DashboardCard from '@/components/ui/DashboardCard';
import { dashboardService } from '@/services/dashboardService';
import { AdminDashboardData } from '@/types';
import { useBranding } from '@/context/BrandingContext';

const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { branding } = useBranding();
  const modules = branding.modules ?? {};
  const isModuleEnabled = (enabled?: boolean) => enabled === true;

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const dashboardData = await dashboardService.getAdminDashboard();
        setData(dashboardData);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Error loading dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const handleExpiredUsersClick = () => {
    navigate('/users?status=expired');
  };

  const handleUsersClick = () => {
    navigate('/users');
  };

  const handleUsersAboutToExpireClick = () => {
    navigate('/users?status=aboutToExpire');
  };

  const handleAgentsClick = () => {
    navigate('/users?role=agent');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Administración</h1>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Administración</h1>
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Panel de Administración</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <DashboardCard
            onClick={handleUsersClick}
            isClickable={true}
            title="Total de Usuarios"
            value={data.totalUsers.toLocaleString()}
            icon={<Users className="h-8 w-8" />}
          />

          <DashboardCard
            title="Total de Agentes"
            isClickable={true}
            onClick={handleAgentsClick}
            value={data.totalAgents.toLocaleString()}
            icon={<UserCheck className="h-8 w-8" />}
          />

          <DashboardCard
            title="Usuarios Vencidos"
            value={data.expiredUsers.toLocaleString()}
            subtitle="Haz clic para ver la lista"
            icon={<UserX className="h-8 w-8" />}
            onClick={handleExpiredUsersClick}
            isClickable={true}
          />

          <DashboardCard
            title="Usuarios por Vencer"
            value={data.usersAboutToExpire.toLocaleString()}
            subtitle="En el mes actual"
            icon={<Calendar className="h-8 w-8" />}
            onClick={handleUsersAboutToExpireClick}
            isClickable={true}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Trophy className="h-6 w-6 mr-2" />
              Top 5 Agentes Vendedores
            </h2>
            <div className="space-y-4">
              {data.topSellingAgents.length > 0 ? (
                data.topSellingAgents.map((agent, index) => (
                  <div key={agent.username} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3 ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-gray-400' : 
                        index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{agent.fullName}</p>
                        <p className="text-sm text-gray-500">{agent.username}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{agent.totalSales.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">ventas</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center">No hay datos disponibles</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Play className="h-6 w-6 mr-2" />
              Contenido Activo por Tipo
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {isModuleEnabled(modules.movies) && (
                <div
                  onClick={() => navigate('/content/movies')}
                  className="text-center p-4 bg-blue-50 rounded-md cursor-pointer hover:shadow-md transition-shadow">
                  <Film className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="text-2xl font-bold text-blue-900">{data.contentCounts.movies.toLocaleString()}</p>
                  <p className="text-sm text-blue-600">Películas</p>
                </div>
              )}

              {isModuleEnabled(modules.tvShows) && (
                <div
                  onClick={() => navigate('/content/tv-shows')}
                  className="text-center p-4 bg-green-50 rounded-md cursor-pointer hover:shadow-md transition-shadow">
                  <Tv className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-green-900">{data.contentCounts.series.toLocaleString()}</p>
                  <p className="text-sm text-green-600">Series</p>
                </div>
              )}

              {isModuleEnabled(modules.tvShows) && (
                <div className="text-center p-4 bg-purple-50 rounded-md">
                  <Play className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <p className="text-2xl font-bold text-purple-900">{data.contentCounts.episodes.toLocaleString()}</p>
                  <p className="text-sm text-purple-600">Episodios</p>
                </div>
              )}

              {isModuleEnabled(modules.concerts) && (
                <div
                  onClick={() => navigate('/content/concerts')}
                  className="text-center p-4 bg-pink-50 rounded-md cursor-pointer hover:shadow-md transition-shadow">
                  <MicVocal className="h-8 w-8 mx-auto mb-2 text-pink-600" />
                  <p className="text-2xl font-bold text-pink-900">{data.contentCounts.concerts.toLocaleString()}</p>
                  <p className="text-sm text-pink-600">Conciertos</p>
                </div>
              )}

              {isModuleEnabled(modules.sports) && (
                <div
                  onClick={() => navigate('/content/sports')}
                  className="text-center p-4 bg-yellow-50 rounded-md cursor-pointer hover:shadow-md transition-shadow">
                  <Volleyball className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
                  <p className="text-2xl font-bold text-yellow-900">{data.contentCounts.sports.toLocaleString()}</p>
                  <p className="text-sm text-yellow-600">Deportes</p>
                </div>
              )}

              {isModuleEnabled(modules.documentaries) && (
                <div
                  onClick={() => navigate('/content/documentaries')}
                  className="text-center p-4 bg-indigo-50 rounded-md cursor-pointer hover:shadow-md transition-shadow">
                  <Clapperboard className="h-8 w-8 mx-auto mb-2 text-indigo-600" />
                  <p className="text-2xl font-bold text-indigo-900">{data.contentCounts.documentaries.toLocaleString()}</p>
                  <p className="text-sm text-indigo-600">Documentales</p>
                </div>
              )}

              {isModuleEnabled(modules.tvChannels) && (
                <div
                  onClick={() => navigate('/content/tv-channels')}
                  className="text-center p-4 bg-red-50 rounded-md cursor-pointer hover:shadow-md transition-shadow">
                  <Tv className="h-8 w-8 mx-auto mb-2 text-red-600" />
                  <p className="text-2xl font-bold text-red-900">{data.contentCounts.tvChannels.toLocaleString()}</p>
                  <p className="text-sm text-red-600">Canales de TV</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
